/* =========================================================================
   HCS Cart Bundle + Protection Plan Quantity Sync Engine
   -------------------------------------------------------------------------
   Exposes window.HCS_BUNDLE_SYNC with helpers for cascading quantity from
   a parent cart line to its bundle children AND protection plan(s).

   Relationships (line-item properties written at add-to-cart time):
     Bundle child → parent:
       _Bundle Parent ID  (numeric product ID, preferred, rock-solid)
       _Bundle Parent     (product title, HTML-encoded, fallback)
     Protection Plan → parent:
       _Protected Product (parent product title)

   Behavior:
     - cascadeParentQty: sets parent + all related children + PPs to newQty
       in a single atomic /cart/update.js POST (no race, no stale reads).
     - On newQty === 0, removes parent + all related lines together.
     - Independent child qty edits still work — call updateSingleLine(key,qty)
       which does NOT cascade (per spec: manual child edit doesn't
       back-propagate to parent).
     - Dispatches "hcs:cart-cascade" event so other UI (minicart, summary)
       can re-render.
   ========================================================================= */
(function() {
  if (window.HCS_BUNDLE_SYNC) return;

  /* ── HTML-entity decode for line item titles ── */
  function decodeTitle(s) {
    if (!s) return '';
    return String(s)
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&amp;/g, '&')
      .trim();
  }

  /* ── Find children + PPs in cart that link to a parent product ── */
  function findRelatedLineKeys(cart, parentProductId, parentTitle) {
    var pidStr = String(parentProductId || '').trim();
    var pTitle = decodeTitle(parentTitle);
    var children = [];
    var pp = [];
    if (!cart || !cart.items) return { children: children, pp: pp };
    cart.items.forEach(function(item) {
      var props = item.properties || {};
      var bpId = (props['_Bundle Parent ID'] || '').toString().trim();
      var bp = decodeTitle(props['_Bundle Parent']);
      var protProd = decodeTitle(props['_Protected Product']);

      /* PP match: title only (PP doesn't have _Bundle Parent) */
      if (pTitle && protProd && protProd === pTitle) {
        pp.push(item.key);
        return;
      }
      /* Bundle child: ID preferred, title fallback */
      if (pidStr && bpId && bpId === pidStr) {
        children.push(item.key);
      } else if (pTitle && bp && bp === pTitle) {
        children.push(item.key);
      }
    });
    return { children: children, pp: pp };
  }

  /* ── Fetch cart (small local cache to batch rapid calls within 80ms) ── */
  var _cartCache = null;
  var _cartCacheTime = 0;
  function fetchCart(forceRefresh) {
    var now = Date.now();
    if (!forceRefresh && _cartCache && (now - _cartCacheTime < 80)) {
      return Promise.resolve(_cartCache);
    }
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        _cartCache = cart;
        _cartCacheTime = Date.now();
        return cart;
      });
  }
  function invalidateCache() { _cartCache = null; _cartCacheTime = 0; }

  /* ── Cascade parent qty to parent + children + PPs atomically ──
       If newQty === 0, removes every matched line.
       Returns promise resolving to { updated: N, cart: newCart }. */
  function cascadeParentQty(parentKey, parentProductId, parentTitle, newQty) {
    var qty = Math.max(0, parseInt(newQty, 10) || 0);
    return fetchCart(true).then(function(cart) {
      var related = findRelatedLineKeys(cart, parentProductId, parentTitle);
      var allKeys = [];
      if (parentKey) allKeys.push(parentKey);
      related.children.forEach(function(k) { if (allKeys.indexOf(k) < 0) allKeys.push(k); });
      related.pp.forEach(function(k) { if (allKeys.indexOf(k) < 0) allKeys.push(k); });
      if (!allKeys.length) return { updated: 0, cart: cart };

      var updates = {};
      allKeys.forEach(function(k) { updates[k] = qty; });

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function(r) { return r.json(); })
        .then(function(resultCart) {
          invalidateCache();
          document.dispatchEvent(new CustomEvent('hcs:cart-cascade', {
            detail: {
              parentKey: parentKey,
              parentProductId: parentProductId,
              newQty: qty,
              childKeys: related.children,
              ppKeys: related.pp,
              cart: resultCart
            }
          }));
          return { updated: allKeys.length, cart: resultCart };
        });
    });
  }

  /* ── Update a SINGLE line without cascading ──
       Use for bundle-child qty adjustments where back-propagation is unwanted. */
  function updateSingleLine(key, qty) {
    var q = Math.max(0, parseInt(qty, 10) || 0);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: q })
    }).then(function(r) { return r.json(); })
      .then(function(cart) {
        invalidateCache();
        document.dispatchEvent(new CustomEvent('hcs:cart-line-updated', {
          detail: { key: key, newQty: q, cart: cart }
        }));
        return cart;
      });
  }

  /* ── Read parent qty from the DOM row ── */
  function readParentQtyFromRow(row) {
    if (!row) return 1;
    var input = row.querySelector('[data-cd-qty-input], [data-cm4-qty-input], [data-mc-qty-input], input.cd-qty-input, input.cm4__qty-input, input[type="number"]');
    if (input) {
      var v = parseInt(input.value, 10);
      if (!isNaN(v) && v >= 1) return v;
    }
    var attr = row.getAttribute('data-qty') || row.getAttribute('data-quantity');
    if (attr) {
      var n = parseInt(attr, 10);
      if (!isNaN(n) && n >= 1) return n;
    }
    return 1;
  }

  /* ── Read current parent qty from cart by parent key (source of truth) ── */
  function readParentQtyFromCart(parentKey) {
    return fetchCart(false).then(function(cart) {
      if (!cart || !cart.items) return 1;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].key === parentKey) return cart.items[i].quantity || 1;
      }
      return 1;
    });
  }

  window.HCS_BUNDLE_SYNC = {
    decodeTitle: decodeTitle,
    fetchCart: fetchCart,
    findRelatedLineKeys: findRelatedLineKeys,
    cascadeParentQty: cascadeParentQty,
    updateSingleLine: updateSingleLine,
    readParentQtyFromRow: readParentQtyFromRow,
    readParentQtyFromCart: readParentQtyFromCart,
    invalidateCache: invalidateCache
  };
})();
